import { useCallback, useEffect, useState } from "react";
import { Send, Loader2, Mail, CheckCircle2, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import { AlertDialog, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { api, apiError } from "@/lib/api";
import { fmt } from "@/lib/demo/utils";
import { useI18n } from "@/lib/i18n/i18n";

const ERR = { no_recipients: "Sin administrador con email válido", insufficient_credits: "Saldo de email insuficiente", integration_disabled: "Email desactivado", daily_limit_reached: "Límite diario de email alcanzado", interrupted: "Interrumpido" };

const LastStatus = ({ last, testId }) => {
  const { t, lang } = useI18n();
  if (!last) return <span className="text-xs text-slate-400" data-testid={testId}>{t("No enviado")}</span>;
  const ok = last.status === "sent";
  return (
    <span className={`flex items-center gap-1.5 text-xs font-semibold ${ok ? "text-emerald-700" : "text-amber-700"}`} data-testid={testId}>
      {ok ? <CheckCircle2 className="h-3.5 w-3.5" /> : <AlertCircle className="h-3.5 w-3.5" />}
      {ok ? new Date(last.sentAt).toLocaleString(lang, { dateStyle: "short", timeStyle: "short" }) : t(ERR[last.error] || "Error al enviar")}
    </span>
  );
};

const JobBar = ({ job }) => {
  const { t } = useI18n();
  if (!job) return null;
  const done = job.sent + job.failed + job.skipped;
  return (
    <div className="mt-4 rounded-2xl bg-slate-50 p-4 text-xs" data-testid="reports-job">
      <div className="flex justify-between font-semibold"><span>{t(job.status === "running" ? "Enviando informes…" : job.status === "paused" ? "Envío pausado" : "Último envío completado")}</span><span>{done} / {job.total}</span></div>
      <div className="mt-2 h-2 rounded-full bg-slate-200"><div className="h-2 rounded-full bg-upv transition-[width]" style={{ width: `${(done / Math.max(1, job.total)) * 100}%` }} /></div>
      <p className="mt-2 text-slate-500" data-testid="reports-job-counts">{t("{s} enviados · {k} sin destinatario · {f} con error", { s: job.sent, k: job.skipped, f: job.failed })}{job.error && ERR[job.error] ? ` · ${t(ERR[job.error])}` : ""}</p>
    </div>
  );
};

export const ReportsPanel = () => {
  const { t } = useI18n();
  const [data, setData] = useState(null);
  const [confirm, setConfirm] = useState(false);
  const [busy, setBusy] = useState(null);
  const load = useCallback(() => api.get("/admin/reports").then((r) => setData(r.data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);
  const running = data?.job?.status === "running";
  useEffect(() => { if (!running) return undefined; const id = setInterval(load, 2000); return () => clearInterval(id); }, [running, load]);

  const send = async (body, key) => {
    setBusy(key);
    try { await api.post("/admin/reports/send", body); toast.success(t("Envío de informes iniciado")); setConfirm(false); await load(); } catch (e) { toast.error(t(apiError(e).message)); } finally { setBusy(null); }
  };
  if (!data) return null;
  const pending = data.items.filter((c) => !c.everSent && c.recipients > 0).length;

  return (
    <section className="rounded-[28px] border border-slate-200 bg-white p-6 sm:p-8" data-testid="backoffice-reports">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h2 className="font-display text-2xl font-bold">{t("Resumen post-evento por email")}</h2>
          <p className="mt-1 max-w-xl text-sm text-slate-500">{t("Cada empresa recibe sus cifras, el origen de los CV y un enlace al portal para descargarlos. Se envía a los administradores de cada empresa.")}</p>
        </div>
        <button onClick={() => setConfirm(true)} disabled={running || !pending} className="flex h-11 shrink-0 items-center gap-2 rounded-xl bg-upv px-5 text-sm font-bold text-white hover:bg-upv-dark disabled:opacity-50" data-testid="reports-send-all"><Send className="h-4 w-4" /> {t("Enviar a {n} pendientes", { n: pending })}</button>
      </div>
      <JobBar job={data.job} />
      <ul className="mt-5 max-h-[420px] divide-y divide-slate-100 overflow-y-auto">
        {data.items.map((c) => (
          <li key={c.id} className="flex flex-col gap-2 py-3 sm:flex-row sm:items-center sm:justify-between" data-testid={`reports-row-${c.slug}`}>
            <div className="min-w-0">
              <p className="truncate font-semibold">{c.name}</p>
              <p className="text-xs text-slate-500">{t("{n} candidatos", { n: fmt(c.candidates) })} · {t("{n} destinatarios", { n: c.recipients })}</p>
            </div>
            <div className="flex items-center gap-3">
              <LastStatus last={c.last} testId={`reports-status-${c.slug}`} />
              <button onClick={() => send({ companyIds: [c.id], resend: true }, c.id)} disabled={running || busy === c.id} title={t(c.everSent ? "Reenviar" : "Enviar")} className="flex h-9 items-center gap-1.5 rounded-lg border border-slate-200 px-3 text-xs font-bold hover:border-slate-900 disabled:opacity-50" data-testid={`reports-send-${c.slug}`}>
                {busy === c.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Mail className="h-3.5 w-3.5" />} {t(c.everSent ? "Reenviar" : "Enviar")}
              </button>
            </div>
          </li>
        ))}
      </ul>
      <AlertDialog open={confirm} onOpenChange={(o) => !busy && setConfirm(o)}>
        <AlertDialogContent className="rounded-[28px]" data-testid="reports-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display text-2xl font-bold">{t("¿Enviar el resumen a {n} empresas?", { n: pending })}</AlertDialogTitle>
            <AlertDialogDescription>{t("Se enviará un email real a los administradores de cada empresa que aún no lo ha recibido.")}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={!!busy} data-testid="reports-confirm-cancel">{t("Cancelar")}</AlertDialogCancel>
            <button onClick={() => send({ resend: false }, "all")} disabled={!!busy} className="inline-flex h-10 items-center justify-center gap-2 rounded-md bg-upv px-5 text-sm font-bold text-white hover:bg-upv-dark disabled:opacity-70" data-testid="reports-confirm-send">{busy === "all" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} {t("Enviar")}</button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </section>
  );
};
